import { useLocation, useNavigate, useParams } from 'react-router'

import { useSession } from '@/auth/session'
import { clientesVisiveis } from '@/tenant/clientes'
import { notificarTrocaDeCliente } from '@/tenant/trocaDeCliente'

/**
 * Global client selector — shot `2c`, top right of the module frame.
 *
 * Story 0.2 built the control; story 0.5 gave it its behaviour. What changed:
 * the options come from the user's `{clientes}` (AC-2) instead of a stub list, the
 * switch notifies the tenant-change seam so the query cache is invalidated (AC-5,
 * AC-9), and the disabled single-client case is now reachable by a real persona
 * rather than only in principle.
 *
 * 🔒 **A user with exactly one client still sees it, disabled, at the same width**
 * (AC-6). Hiding it would shift the whole header between personas, and §5.2 asks
 * for the opposite — the bar must not move.
 *
 * ⚠️ That is also why the width is FIXED rather than left to the control. A native
 * `<select>` sizes itself to its longest option, so the module frame's right slot
 * measured 208px for a persona with one client and 282px for one with two — the
 * header visibly shifted between personas, which is the exact thing AC-6 forbids.
 * Browser-caught during story 0.5; the unit test named "a barra não desloca" was
 * asserting only `disabled` and would never have found it.
 */
export function ClienteSelector() {
  const { idCliente } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { sessao, perfil } = useSession()

  // A sessão diz QUAIS clientes o usuário alcança; o `GET /eu` diz COMO ELES SE
  // CHAMAM. `clientesVisiveis` já mantinha essa separação e só faltava alguém lhe
  // entregar os nomes reais — até aqui vinham do stub compilado no bundle.
  const clientes = clientesVisiveis(sessao?.clientes ?? [], perfil?.clientes ?? [])
  const unicoCliente = clientes.length <= 1

  async function trocarCliente(novoId: string) {
    if (!idCliente || novoId === idCliente) return

    // Order matters: invalidate BEFORE navigating. The new screen mounts and fires
    // its queries immediately, and a cache still holding the previous tenant would
    // serve one frame of the wrong client's numbers before the refetch lands —
    // which is exactly the mistake BR-19 exists to prevent, and the hardest kind to
    // notice because it self-corrects.
    await notificarTrocaDeCliente(novoId)

    // Swap the client segment in place, keeping the module and tab the user is on.
    navigate(pathname.replace(`/cliente/${idCliente}/`, `/cliente/${novoId}/`))
  }

  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">Cliente</span>
      <i
        className="ph ph-buildings text-iox-navy pointer-events-none absolute left-4 text-lg"
        aria-hidden="true"
      />
      <select
        value={idCliente ?? ''}
        disabled={unicoCliente}
        onChange={(evento) => void trocarCliente(evento.target.value)}
        // `w-72` + `truncate`: fixed slot, long tenant names ellipsise instead of
        // widening the header. See the note above — this is AC-6, not styling.
        className="rounded-control bg-navy-50 text-iox-navy border-navy-100 w-72 truncate appearance-none border py-2.5 pr-10 pl-11 type-label transition-colors duration-base ease-out disabled:opacity-70"
      >
        {clientes.map((cliente) => (
          <option key={cliente.idCliente} value={cliente.idCliente}>
            {cliente.nome}
          </option>
        ))}
      </select>
      <i
        className="ph ph-caret-down text-iox-navy pointer-events-none absolute right-4 text-sm"
        aria-hidden="true"
      />
    </label>
  )
}
